import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Users, DollarSign, Eye, Copy, ExternalLink,
  LogOut, Sun, Moon, Globe, Upload, Clock, CheckCircle, AlertCircle,
  Settings, User, KeyRound, Bell, Save, ArrowLeft, EyeOff
} from 'lucide-react';

const t = (lang, de, en) => (lang === 'de' ? de : en);
const API = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  inquiry: { de: 'Anfrage', en: 'Inquiry', color: 'bg-blue-500/20 text-blue-400' },
  consultation: { de: 'Beratung', en: 'Consultation', color: 'bg-yellow-500/20 text-yellow-400' },
  treatment_done: { de: 'Behandlung abgeschlossen', en: 'Treatment completed', color: 'bg-green-500/20 text-green-400' },
  paid: { de: 'Ausgezahlt', en: 'Paid', color: 'bg-emerald-500/20 text-emerald-400' },
};

const PartnerDashboard = () => {
  const { isDark, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Settings state
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '', social_media: '' });
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [dailySummary, setDailySummary] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);

  const token = localStorage.getItem('partnerToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!token) { navigate('/partner/login'); return; }
    try {
      const [profileRes, referralsRes] = await Promise.all([
        fetch(`${API}/api/partners/me`, { headers }),
        fetch(`${API}/api/partners/referrals`, { headers }),
      ]);
      if (!profileRes.ok) { localStorage.removeItem('partnerToken'); navigate('/partner/login'); return; }
      const p = await profileRes.json();
      setPartner(p);
      setProfileForm({ name: p.name, email: p.email, phone: p.phone || '', social_media: p.social_media || '' });
      setDailySummary(p.notification_settings?.daily_summary || false);
      const refData = await referralsRes.json();
      setReferrals(refData.referrals || []);
    } catch {
      toast.error(t(language, 'Fehler beim Laden', 'Error loading data'));
    } finally {
      setLoading(false);
    }
  }, [token, navigate, language]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const promoUrl = partner ? `${window.location.origin}/promo/${partner.slug}` : '';

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t(language, 'Kopiert!', 'Copied!'));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/partners/logo`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (!res.ok) throw new Error();
      toast.success(t(language, 'Logo hochgeladen!', 'Logo uploaded!'));
      fetchData();
    } catch {
      toast.error(t(language, 'Upload fehlgeschlagen', 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await fetch(`${API}/api/partners/profile`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      toast.success(t(language, 'Profil gespeichert', 'Profile saved'));
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(t(language, 'Passwörter stimmen nicht überein', 'Passwords do not match'));
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error(t(language, 'Mind. 6 Zeichen', 'Min. 6 characters'));
      return;
    }
    setSavingPassword(true);
    try {
      const res = await fetch(`${API}/api/partners/password`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_password: passwordForm.old_password, new_password: passwordForm.new_password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      toast.success(t(language, 'Passwort geändert', 'Password changed'));
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPassword(false);
    }
  };

  const saveNotifications = async () => {
    setSavingNotifications(true);
    try {
      const res = await fetch(`${API}/api/partners/notifications`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ daily_summary: dailySummary }),
      });
      if (!res.ok) throw new Error();
      toast.success(t(language, 'Einstellungen gespeichert', 'Settings saved'));
    } catch {
      toast.error(t(language, 'Fehler', 'Error'));
    } finally {
      setSavingNotifications(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('partnerToken');
    localStorage.removeItem('partnerData');
    navigate('/partner/login');
  };

  if (loading) return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      <svg className={`animate-spin w-8 h-8 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="60 30" /></svg>
    </div>
  );

  if (!partner) return null;

  const stats = [
    { icon: <Eye className="w-5 h-5" />, label: t(language, 'Klicks', 'Clicks'), value: partner.stats.clicks, color: 'text-blue-400' },
    { icon: <Users className="w-5 h-5" />, label: t(language, 'Anfragen', 'Inquiries'), value: partner.stats.inquiries, color: 'text-purple-400' },
    { icon: <CheckCircle className="w-5 h-5" />, label: t(language, 'Behandlungen', 'Treatments'), value: partner.stats.treatments, color: 'text-green-400' },
    { icon: <DollarSign className="w-5 h-5" />, label: t(language, 'Verdient', 'Earned'), value: `${partner.stats.earned.toFixed(0)}€`, color: 'text-emerald-400' },
  ];

  const tabs = [
    { id: 'dashboard', icon: <Eye className="w-4 h-4" />, label: t(language, 'Dashboard', 'Dashboard') },
    { id: 'settings', icon: <Settings className="w-4 h-4" />, label: t(language, 'Einstellungen', 'Settings') },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-500 ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-40 backdrop-blur-xl border-b ${isDark ? 'bg-[#0a0a0a]/90 border-white/5' : 'bg-white/90 border-gray-200'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/partner">
            <img src="https://customer-assets.emergentagent.com/job_dion-hair/artifacts/cm40t14d_DionHairClinic_Logo.svg" alt="Dion Hair Clinic" className={`h-8 w-auto ${isDark ? 'brightness-0 invert' : ''}`} />
          </Link>
          <div className="flex items-center gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${activeTab === tab.id ? (isDark ? 'bg-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]') : (isDark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-700')}`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
            <div className="w-px h-5 mx-1 bg-white/10" />
            <button onClick={toggleLanguage} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'}`}><Globe className="w-4 h-4" /></button>
            <button onClick={toggleTheme} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'}`}>{isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</button>
            <button onClick={logout} data-testid="partner-logout" className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'}`}><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* ============ DASHBOARD TAB ============ */}
        {activeTab === 'dashboard' && (
          <>
            {/* Welcome */}
            <div className="mb-8">
              <h1 className={`text-2xl sm:text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t(language, `Hallo, ${partner.name}`, `Hello, ${partner.name}`)}
              </h1>
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t(language, 'Dein Partner-Dashboard', 'Your Partner Dashboard')}
              </p>
            </div>

            {/* Promo Link & Code */}
            <div className={`rounded-2xl p-5 sm:p-6 border mb-6 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`text-xs uppercase tracking-wider mb-2 block ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                    {t(language, 'Dein Promo-Link', 'Your Promo Link')}
                  </label>
                  <div className="flex items-center gap-2">
                    <div className={`flex-1 px-3 py-2 rounded-lg text-sm truncate ${isDark ? 'bg-white/5 text-white/70' : 'bg-gray-50 text-gray-600'}`} data-testid="partner-promo-url">{promoUrl}</div>
                    <button onClick={() => copyToClipboard(promoUrl)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'}`} data-testid="copy-promo-link"><Copy className="w-4 h-4" /></button>
                    <a href={promoUrl} target="_blank" rel="noopener noreferrer" className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'}`}><ExternalLink className="w-4 h-4" /></a>
                  </div>
                </div>
                <div>
                  <label className={`text-xs uppercase tracking-wider mb-2 block ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                    {t(language, 'Dein Promo-Code', 'Your Promo Code')}
                  </label>
                  <div className="flex items-center gap-2">
                    <div className={`flex-1 px-3 py-2 rounded-lg text-sm font-mono font-bold ${isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'}`} data-testid="partner-promo-code">{partner.promo_code}</div>
                    <button onClick={() => copyToClipboard(partner.promo_code)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'}`}><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {stats.map((s, i) => (
                <div key={i} className={`rounded-2xl p-5 border ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                  <div className={`mb-2 ${s.color}`}>{s.icon}</div>
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.value}</div>
                  <div className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Pending earnings */}
            {partner.stats.pending > 0 && (
              <div className={`rounded-2xl p-5 border mb-8 ${isDark ? 'bg-yellow-500/5 border-yellow-500/20' : 'bg-yellow-50 border-yellow-200'}`}>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-yellow-500" />
                  <div>
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{partner.stats.pending.toFixed(0)}€</span>
                    <span className={`ml-2 text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t(language, 'ausstehende Provision', 'pending commission')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Logo Upload */}
            <div className={`rounded-2xl p-5 sm:p-6 border mb-8 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
              <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t(language, 'Dein Partner-Logo', 'Your Partner Logo')}</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t(language, 'Wird auf deiner Promo-Seite angezeigt.', 'Displayed on your promo page.')}</p>
              <div className="flex items-center gap-4">
                {partner.logo_url && <img src={partner.logo_url} alt="Logo" className="h-16 w-16 object-contain rounded-xl border border-white/10" />}
                <label className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${isDark ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                  <Upload className="w-4 h-4" />
                  {uploading ? t(language, 'Lädt...', 'Uploading...') : t(language, 'Logo hochladen', 'Upload Logo')}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="partner-logo-upload" />
                </label>
              </div>
            </div>

            {/* Referrals Table */}
            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
              <div className="px-5 sm:px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t(language, 'Deine Empfehlungen', 'Your Referrals')}</h3>
                <span className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{referrals.length}</span>
              </div>
              {referrals.length === 0 ? (
                <div className="p-8 text-center">
                  <AlertCircle className={`w-8 h-8 mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                  <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Noch keine Empfehlungen. Teile deinen Link!', 'No referrals yet. Share your link!')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className={isDark ? 'text-white/40' : 'text-gray-400'}>
                        <th className="px-5 py-3 font-medium">{t(language, 'Name', 'Name')}</th>
                        <th className="px-5 py-3 font-medium hidden sm:table-cell">{t(language, 'E-Mail', 'Email')}</th>
                        <th className="px-5 py-3 font-medium">{t(language, 'Status', 'Status')}</th>
                        <th className="px-5 py-3 font-medium hidden sm:table-cell">{t(language, 'Provision', 'Commission')}</th>
                        <th className="px-5 py-3 font-medium hidden md:table-cell">{t(language, 'Datum', 'Date')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((r) => {
                        const st = statusLabels[r.status] || statusLabels.inquiry;
                        return (
                          <tr key={r.id} className={`border-t ${isDark ? 'border-white/5' : 'border-gray-50'}`}>
                            <td className={`px-5 py-3 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{r.client_name}</td>
                            <td className={`px-5 py-3 hidden sm:table-cell ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{r.client_email}</td>
                            <td className="px-5 py-3">
                              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${st.color}`}>{language === 'de' ? st.de : st.en}</span>
                            </td>
                            <td className={`px-5 py-3 hidden sm:table-cell font-medium ${r.status === 'paid' ? 'text-emerald-400' : isDark ? 'text-white/40' : 'text-gray-400'}`}>{r.commission}€</td>
                            <td className={`px-5 py-3 hidden md:table-cell ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{new Date(r.created_at).toLocaleDateString('de-DE')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ============ SETTINGS TAB ============ */}
        {activeTab === 'settings' && (
          <>
            <div className="mb-8">
              <h1 className={`text-2xl sm:text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t(language, 'Einstellungen', 'Settings')}
              </h1>
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t(language, 'Profil, Passwort & Benachrichtigungen verwalten', 'Manage profile, password & notifications')}
              </p>
            </div>

            {/* Profile Section */}
            <div className={`rounded-2xl p-5 sm:p-6 border mb-6 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                  <User className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t(language, 'Profil bearbeiten', 'Edit Profile')}</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={`text-xs block mb-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Name', 'Name')}</label>
                  <Input value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid="settings-name" />
                </div>
                <div>
                  <label className={`text-xs block mb-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'E-Mail', 'Email')}</label>
                  <Input type="email" value={profileForm.email} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))} className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid="settings-email" />
                </div>
                <div>
                  <label className={`text-xs block mb-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Telefon', 'Phone')}</label>
                  <Input value={profileForm.phone} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid="settings-phone" />
                </div>
                <div>
                  <label className={`text-xs block mb-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Social Media', 'Social Media')}</label>
                  <Input value={profileForm.social_media} onChange={e => setProfileForm(f => ({ ...f, social_media: e.target.value }))} className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid="settings-social" />
                </div>
              </div>
              <Button onClick={saveProfile} disabled={savingProfile} className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white" data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-1.5" />
                {savingProfile ? t(language, 'Speichern...', 'Saving...') : t(language, 'Profil speichern', 'Save Profile')}
              </Button>
            </div>

            {/* Password Section */}
            <div className={`rounded-2xl p-5 sm:p-6 border mb-6 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
                  <KeyRound className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t(language, 'Passwort ändern', 'Change Password')}</h3>
              </div>
              <div className="space-y-3 mb-4 max-w-md">
                <div>
                  <label className={`text-xs block mb-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Altes Passwort', 'Current Password')}</label>
                  <div className="relative">
                    <Input type={showOldPw ? 'text' : 'password'} value={passwordForm.old_password} onChange={e => setPasswordForm(f => ({ ...f, old_password: e.target.value }))} className={isDark ? 'bg-white/5 border-white/10 text-white pr-10' : 'pr-10'} data-testid="settings-old-password" />
                    <button type="button" onClick={() => setShowOldPw(!showOldPw)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                      {showOldPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className={`text-xs block mb-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Neues Passwort', 'New Password')}</label>
                  <div className="relative">
                    <Input type={showNewPw ? 'text' : 'password'} value={passwordForm.new_password} onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))} className={isDark ? 'bg-white/5 border-white/10 text-white pr-10' : 'pr-10'} data-testid="settings-new-password" />
                    <button type="button" onClick={() => setShowNewPw(!showNewPw)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                      {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className={`text-xs block mb-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t(language, 'Passwort bestätigen', 'Confirm Password')}</label>
                  <Input type="password" value={passwordForm.confirm_password} onChange={e => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))} className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid="settings-confirm-password" />
                </div>
              </div>
              <Button onClick={changePassword} disabled={savingPassword} className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white" data-testid="change-password-btn">
                <KeyRound className="w-4 h-4 mr-1.5" />
                {savingPassword ? t(language, 'Ändern...', 'Changing...') : t(language, 'Passwort ändern', 'Change Password')}
              </Button>
            </div>

            {/* Notifications Section */}
            <div className={`rounded-2xl p-5 sm:p-6 border mb-6 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-green-500/10' : 'bg-green-50'}`}>
                  <Bell className="w-5 h-5 text-green-400" />
                </div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t(language, 'Benachrichtigungen', 'Notifications')}</h3>
              </div>
              <div className="mb-4">
                <label className={`flex items-center gap-3 cursor-pointer group`}>
                  <div className="relative">
                    <input type="checkbox" checked={dailySummary} onChange={e => setDailySummary(e.target.checked)} className="sr-only peer" data-testid="settings-daily-summary" />
                    <div className={`w-11 h-6 rounded-full transition-colors peer-checked:bg-[#7BA7C2] ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} />
                    <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                  </div>
                  <div>
                    <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {t(language, 'Tägliche Zusammenfassung', 'Daily Summary')}
                    </div>
                    <div className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {t(language, 'Erhalte jeden Abend eine E-Mail mit Klicks und neuen Anfragen des Tages.', 'Receive a daily email with clicks and new inquiries.')}
                    </div>
                  </div>
                </label>
              </div>
              <Button onClick={saveNotifications} disabled={savingNotifications} className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white" data-testid="save-notifications-btn">
                <Save className="w-4 h-4 mr-1.5" />
                {savingNotifications ? t(language, 'Speichern...', 'Saving...') : t(language, 'Speichern', 'Save')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PartnerDashboard;
